/**
 * Pure mapping: the realtime voice agent's StoryBrief → a ContentParameters
 * patch. Extracted from the wizard so the "fill everything behind the scenes"
 * step is deterministically testable (no React, no OpenAI).
 *
 * The voice session collects a free-form brief; here we fold it into the SAME
 * ContentParameters the typed/from-text paths produce, so downstream generation
 * (buildCreateParams → ContentPlanExecutor) is identical regardless of input mode.
 * Structured fields the child answered (genre, age band, length, energy) are
 * normalised to the wizard's real enums and set explicitly — not just dropped in
 * the prompt text — so the output/review step reflects them. Heroes picked in the
 * earlier step (characterIds) are preserved by the caller's spread.
 */

import { locales, type Locale } from '@/shared/i18n';
import type { StoryBrief } from '@/contexts/media/realtimeVoice';
import type { ContentParameters } from '../components/ContentCreationWizard/ParametersStep';

// The wizard's real option enums (mirror ParametersStep). Mapping targets — a
// brief value that can't be mapped is left to the caller's existing value.
const GENRES = [
  'lullaby',
  'adventure',
  'comedy',
  'fable',
  'mystery',
  'everyday',
  'educational',
  'classic',
] as const;
type Genre = (typeof GENRES)[number];

const AGE_GROUPS = ['3-5', '5-7', '7-10', '10+'] as const;
type AgeGroup = (typeof AGE_GROUPS)[number];

function isLocale(v: string | undefined): v is Locale {
  return !!v && (locales as readonly string[]).includes(v);
}

/**
 * Map free-text genre/theme hints to one of the wizard genres. Ordered so more
 * specific kinds win before the broad "magical → fable" catch (a scary-magic
 * story is a mystery, a magical adventure is an adventure). Multilingual-ish.
 */
export function normalizeGenre(...texts: Array<string | undefined>): Genre | undefined {
  const hay = texts.filter(Boolean).join(' ').toLowerCase().trim();
  if (!hay) return undefined;
  const rules: Array<[RegExp, Genre]> = [
    [/lullab|bedtime|sleep|good ?night|колыбель|на ночь|засыпа|убаюк|schlaf|dodo/, 'lullaby'],
    [/comed|funny|humor|silly|смешн|весёл|весел|шутл|drôle|lustig|vtipn/, 'comedy'],
    [/advent|journey|quest|приключ|aventur|abenteuer|dobrodruž/, 'adventure'],
    [/myster|spooky|scary|creepy|detect|страшн|загадк|тайн|жутк|détective|grusel|záhad/, 'mystery'],
    [/educ|learn|teach|познава|обуча|учебн|éducat|lehr|vzděláv/, 'educational'],
    [/everyday|slice of life|быт|обычн|повседнев|quotidien|alltag|všední/, 'everyday'],
    [/classic|folk|классич|народн|classiqu|klassik|klasick/, 'classic'],
    [/fable|fairy|magic|волшебн|сказк|фэнтези|fantasy|чудес|conte|märchen|kouzeln|pohádk/, 'fable'],
  ];
  for (const [re, g] of rules) if (re.test(hay)) return g;
  if ((GENRES as readonly string[]).includes(hay)) return hay as Genre;
  return undefined;
}

/** Map a free-text audience hint to one of the wizard age bands. */
export function normalizeAgeGroup(text: string | undefined): AgeGroup | undefined {
  if (!text) return undefined;
  const s = text.toLowerCase();
  for (const a of AGE_GROUPS) if (s.includes(a)) return a;
  const m = s.match(/(\d{1,2})/);
  if (m) {
    const n = Number(m[1]);
    if (n <= 4) return '3-5';
    if (n <= 6) return '5-7';
    if (n <= 9) return '7-10';
    return '10+';
  }
  if (/little|tiny|toddler|baby|малыш|маленьк|petit|klein|mal[ýé]/.test(s)) return '3-5';
  if (/big kid|older|grown|школьн|большой|подрост|grand|groß|velk/.test(s)) return '7-10';
  return undefined;
}

export interface BriefParametersPatch {
  prompt: string;
  language: string;
  title?: string;
  creationMode: 'generate';
  /** Set only when the brief value maps cleanly to a wizard enum/range. */
  genre?: Genre;
  ageGroup?: AgeGroup;
  energy?: number;
  desiredDurationMinutes?: number;
}

/**
 * Build the parameter patch from a brief. `prevLanguage`/`prevTitle` are the
 * current wizard values, kept when the brief omits them.
 */
export function mapBriefToParameters(
  brief: StoryBrief,
  prev: Pick<ContentParameters, 'language' | 'title'>,
): BriefParametersPatch {
  // The prompt IS the user-facing "description" on the review step, so it must
  // stay in the child's language — just the synopsis, no English scaffolding.
  // genre/age/energy/duration are surfaced as their own localised fields below;
  // heroes go to characterIds. Setting/theme are already woven into the synopsis
  // the agent wrote, so we don't append English "genre:/theme:" hint lines.
  const prompt = brief.synopsis.trim();

  // Structured fields — set only when derivable (else keep the wizard's value).
  const genre = normalizeGenre(brief.genre, brief.theme);
  const ageGroup = normalizeAgeGroup(brief.ageGroup);
  const energy =
    typeof brief.energy === 'number' && Number.isFinite(brief.energy)
      ? Math.max(1, Math.min(5, Math.round(brief.energy)))
      : undefined;
  const desiredDurationMinutes =
    typeof brief.desiredDurationMinutes === 'number' && brief.desiredDurationMinutes > 0
      ? Math.max(1, Math.min(20, Math.round(brief.desiredDurationMinutes)))
      : undefined;

  return {
    prompt,
    language: isLocale(brief.language) ? brief.language : prev.language,
    title: brief.title?.trim() || prev.title,
    creationMode: 'generate',
    ...(genre ? { genre } : {}),
    ...(ageGroup ? { ageGroup } : {}),
    ...(energy != null ? { energy } : {}),
    ...(desiredDurationMinutes != null ? { desiredDurationMinutes } : {}),
  };
}
